// Theme.js
import { createTheme } from "@mui/material/styles";

// CSS Custom Properties for font scaling
const createFontScale = (baseSize = 16) => ({
  "--font-scale-xs": `${baseSize * 0.75}px`, // 12px
  "--font-scale-sm": `${baseSize * 0.875}px`, // 14px
  "--font-scale-md": `${baseSize}px`, // 16px
  "--font-scale-lg": `${baseSize * 1.125}px`, // 18px
  "--font-scale-xl": `${baseSize * 1.25}px`, // 20px
  "--font-scale-2xl": `${baseSize * 1.5}px`, // 24px
});

// Shared typography settings for consistency across themes
const sharedTypography = {
  // Tab font settings from darkTheme (used for tabs in both themes)
  h6: {
    fontFamily: "Roboto, sans-serif", // Consistent with darkTheme
    fontWeight: 700, // Bold, as in darkTheme
  },
  // Button font settings from customTheme (used for buttons in both themes)
  button: {
    fontFamily: "Inter, sans-serif", // Consistent with customTheme
    fontWeight: 600, // Semi-bold, as in customTheme
    textTransform: "none", // No uppercase, as in customTheme
  },
};

// Custom theme (light mode by default)
const customTheme = createTheme({
  // Typography settings specific to customTheme, extended with shared settings
  typography: {
    customFontSize: "var(--font-scale-md)", // Use CSS variable
    fontFamily: "Inter, sans-serif", // Default font for customTheme
    h1: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 700, // Bold
      fontSize: "var(--font-scale-2xl)",
    },
    h2: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 600, // Semi-bold
      fontSize: "var(--font-scale-xl)",
    },
    h3: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 500, // Medium
      fontSize: "var(--font-scale-lg)",
    },
    body1: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400, // Regular
      fontSize: "var(--font-scale-md)",
    },
    ...sharedTypography, // Merge shared tab (h6) and button settings
  },
  components: {
    MuiTabs: {
      defaultProps: {
        centered: true,
        scrollButtons: "auto",
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "var(--font-scale-md)",
          fontWeight: "700 !important",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          backgroundColor: "inherit",
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 700,
            fontSize: "var(--font-scale-md)",
            color: "#000000", // Bold black headers
          },
        },
        cell: {
          fontSize: "var(--font-scale-sm)",
          fontWeight: 400, // Normal weight for row content
          color: "#424242", // Dark gray text for grid data
        },
      },
    },
  },
});

// Dark theme
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#1976d2" },
    secondary: { main: "#ff4081" },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
    text: {
      primary: "#ffffff",
      secondary: "#aaaaaa",
    },
  },
  typography: {
    fontFamily: "Roboto, sans-serif",
    ...sharedTypography,
  },
  components: {
    MuiTabs: {
      defaultProps: {
        centered: true,
        scrollButtons: "auto",
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "var(--font-scale-md)",
          fontWeight: "700 !important",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          backgroundColor: "inherit",
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 700,
            fontSize: "var(--font-scale-md)",
          },
        },
        cell: {
          fontSize: "var(--font-scale-sm)",
          fontWeight: 400, // Normal weight for row content
        },
      },
    },
  },
});

export { customTheme, darkTheme, createFontScale };
